import { AlertTriangle, ArrowRight, CheckCircle2, Stethoscope } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Page, PageHeader } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardFooter, CardHeader } from "@/components/ui/card";
import { Callout, ErrorState, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { CopyButton } from "@/components/ui/misc";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError, apiUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  EMAIL_PROVIDERS, emailChanged, emailPayload, emailProblem, emailSections, emailValues,
  type EmailCheck, type EmailHook, type EmailSettings, type EmailValues,
} from "@/lib/email-settings";
import { useEmailCheck, useEmailSettings, useSaveEmailSettings } from "@/lib/email-settings-api";
import { useTenants } from "@/lib/queries";
import { checkSummary, defaultPublicUrl, maskedUrl } from "@/lib/sms-settings";
import { cn, relativeTime } from "@/lib/utils";

/**
 * Email settings (ADR-180 in platform): how the tenant's emails to its contacts leave, on one screen.
 *
 * Top to bottom is the order it is set up in. The provider and its key. The address emails come
 * from, and the reply domain every thread's own reply address is at. Who may be emailed, and how
 * many a day. The URL SendGrid's Inbound Parse posts the replies to, which saving made. A check
 * that reads the SendGrid account and the reply domain's DNS and says what is still wrong.
 *
 * The page shows only what the provider picked has a use for (`emailSections`): nothing but the
 * picker for Off, the addresses and the reply URL for the sandbox, and for SendGrid its key too,
 * then, once it is saved, the check.
 *
 * Admins only, as core-api's routes are. A superadmin with more than one tenant picks the tenant.
 */
export function EmailSettingsPage() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const tenants = useTenants({ enabled: isSuperadmin });
  const pickTenant = isSuperadmin && (tenants.data?.length ?? 0) > 1;
  const [picked, setPicked] = React.useState<number>();
  const tenantId = pickTenant ? (picked ?? tenants.data?.[0]?.id) : undefined;
  // A superadmin's own tenant is nobody's: wait to know which tenants there are.
  const settings = useEmailSettings(tenantId, !isSuperadmin || !tenants.isPending);

  return (
    <Page>
      <PageHeader
        title="Email"
        description="How your emails to contacts leave: the provider, the address they come from, and where the replies arrive."
        action={
          pickTenant && (
            <Select value={String(tenantId)} onValueChange={(value) => setPicked(Number(value))}>
              <SelectTrigger aria-label="Tenant" className="w-52">
                <SelectValue placeholder="Choose tenant" />
              </SelectTrigger>
              <SelectContent>
                {tenants.data?.map((tenant) => (
                  <SelectItem key={tenant.id} value={String(tenant.id)}>
                    {tenant.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        }
      />
      {settings.isPending ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <Spinner className="size-5" />
        </div>
      ) : settings.error || !settings.data ? (
        <ErrorState
          className="min-h-[40vh]"
          error={settings.error}
          title="Could not load the Email settings"
          action={
            <Button variant="outline" onClick={() => void settings.refetch()}>
              Try again
            </Button>
          }
        />
      ) : (
        // Keyed by the tenant and by when the settings were last saved: the form is loaded again for
        // another tenant and after a save, and not each time the settings are fetched, which must
        // not wipe a key being typed.
        <EmailSettingsBody
          key={`${settings.data.tenant_id}:${settings.data.updated_at ?? "new"}`}
          settings={settings.data}
          tenantId={tenantId}
        />
      )}
    </Page>
  );
}

function EmailSettingsBody({ settings, tenantId }: { settings: EmailSettings; tenantId: number | undefined }) {
  const toast = useToast();
  const save = useSaveEmailSettings(tenantId);
  const check = useEmailCheck(tenantId);
  const fallback = defaultPublicUrl(apiUrl(""), window.location.origin);
  // What the form was loaded with, which a change is measured against.
  const [loaded] = React.useState<EmailValues>(() => emailValues(settings, fallback));
  const [values, setValues] = React.useState<EmailValues>(loaded);
  const [touched, setTouched] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const set = (patch: Partial<EmailValues>) => {
    setValues((current) => ({ ...current, ...patch }));
    setSaveError(null);
  };
  const problem = emailProblem(values, settings);
  // Said beside the field it is about, once a save was tried.
  const errorFor = (field: keyof EmailValues) =>
    touched && problem?.field === field ? problem.message : undefined;
  const changed = emailChanged(values, loaded);
  const sendgrid = values.provider === "sendgrid";
  // Only what the provider picked has a use for (`emailSections`).
  const sections = emailSections(values.provider, settings);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (problem) return;
    setSaveError(null);
    try {
      const saved = await save.mutateAsync(emailPayload(values, settings));
      toast.success(
        "Email settings saved",
        saved.provider === "sendgrid"
          ? "SendGrid accepted the key."
          : saved.provider === "sandbox"
            ? "Every email is taken and none is sent."
            : "Nothing is sent.",
      );
    } catch (error) {
      setSaveError(error instanceof ApiError ? error.message : "Could not save the settings. Please try again.");
    }
  };

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <form onSubmit={(event) => void submit(event)}>
          <CardHeader title="Provider" description="Who carries your emails out, and from which address." />
          <CardBody className="space-y-4">
            <div role="radiogroup" aria-label="Provider" className="grid gap-2 sm:grid-cols-3">
              {EMAIL_PROVIDERS.map((option) => {
                const selected = values.provider === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => set({ provider: option.value })}
                    className={cn(
                      "rounded-md border px-3 py-2 text-left transition-colors",
                      selected
                        ? "border-accent-8 bg-accent-2 ring-2 ring-accent-8/25"
                        : "border-border bg-surface hover:border-border-strong hover:bg-surface-hover",
                    )}
                  >
                    <span className="block text-sm font-medium text-text">{option.label}</span>
                    <span className="mt-0.5 block text-xs text-text-muted">{option.description}</span>
                  </button>
                );
              })}
            </div>

            {sections.key && (
              <Field
                label={settings.sendgrid_api_key_set ? "Replace the API key" : "API key"}
                htmlFor="email-api-key"
                required={!settings.sendgrid_api_key_set}
                error={errorFor("apiKey")}
                hint={
                  settings.sendgrid_api_key_set
                    ? "A key is kept. Leave blank to keep it."
                    : "A restricted key with Mail Send, and Inbound Parse read for the check. Checked with SendGrid when you save, and never shown again."
                }
              >
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={values.apiKey}
                  onChange={(event) => set({ apiKey: event.target.value })}
                />
              </Field>
            )}

            {sections.addresses && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="From address"
                  htmlFor="email-from-address"
                  required
                  error={errorFor("fromAddress")}
                  hint="Who every email is from, at a domain authenticated in SendGrid."
                >
                  <Input
                    type="email"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="help@example.com"
                    value={values.fromAddress}
                    onChange={(event) => set({ fromAddress: event.target.value })}
                  />
                </Field>
                <Field
                  label="From name"
                  htmlFor="email-from-name"
                  error={errorFor("fromName")}
                  hint="What a contact's mail shows beside the address."
                >
                  <Input
                    autoComplete="off"
                    maxLength={100}
                    placeholder="Injury Care Line"
                    value={values.fromName}
                    onChange={(event) => set({ fromName: event.target.value })}
                  />
                </Field>
                <Field
                  label="Reply domain"
                  htmlFor="email-reply-domain"
                  required
                  error={errorFor("replyDomain")}
                  hint="A subdomain of its own, only for replies: each thread gets a reply address there. Its MX record points to SendGrid."
                >
                  <Input
                    className="font-mono"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="reply.example.com"
                    value={values.replyDomain}
                    onChange={(event) => set({ replyDomain: event.target.value })}
                  />
                </Field>
                <Field
                  label="Daily limit"
                  htmlFor="email-daily-limit"
                  error={errorFor("dailyLimit")}
                  hint={`Emails a day, counted from midnight Pacific. 0 is no limit. ${settings.sent_today.toLocaleString("en-US")} sent today.`}
                >
                  <Input
                    inputMode="numeric"
                    value={values.dailyLimit}
                    onChange={(event) => set({ dailyLimit: event.target.value })}
                  />
                </Field>
                <Field
                  className="sm:col-span-2"
                  label="Public address"
                  htmlFor="email-public-url"
                  required={sendgrid}
                  error={errorFor("publicUrl")}
                  hint="Where SendGrid reaches this system, up to /hooks. The URL it posts the replies to is built from it."
                >
                  <Input
                    className="font-mono"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="https://console.example.com/api"
                    value={values.publicUrl}
                    onChange={(event) => set({ publicUrl: event.target.value })}
                  />
                </Field>
                <Field
                  className="sm:col-span-2"
                  label="Allowed recipients"
                  htmlFor="email-allowed"
                  required={settings.allowlist_required && sendgrid}
                  error={errorFor("allowed")}
                  hint="One a line: an address, or @ and a domain for every address there. When the list is not empty, nobody else is emailed. Empty is anyone."
                >
                  <Textarea
                    rows={4}
                    spellCheck={false}
                    placeholder={"tester@example.com\n@example.com"}
                    value={values.allowed}
                    onChange={(event) => set({ allowed: event.target.value })}
                  />
                </Field>
                {settings.allowlist_required && (
                  <Callout tone="info" className="sm:col-span-2">
                    This system emails only the addresses on this list.
                  </Callout>
                )}
              </div>
            )}

            {sections.note && <p className="text-xs text-text-muted">{sections.note}</p>}
            {touched && problem && problem.field === null && <Callout tone="danger">{problem.message}</Callout>}
            {saveError && <Callout tone="danger">{saveError}</Callout>}
          </CardBody>
          <CardFooter className="justify-between">
            <span className="text-xs text-text-muted">
              {sendgrid ? "SendGrid is asked about the key when you save. Nothing is saved if it says no." : ""}
            </span>
            <Button type="submit" variant="default" loading={save.isPending} disabled={!changed}>
              Save
            </Button>
          </CardFooter>
        </form>
      </Card>

      {sections.url && settings.incoming && (
        <Card className="overflow-hidden">
          <CardHeader
            title="The URL SendGrid posts the replies to"
            description="Saving made a trigger for the replies. It is on the Triggers page too. The URL has its secret in it."
          />
          <CardBody>
            <HookRow hook={settings.incoming} hasAddress={Boolean(settings.public_url)} />
          </CardBody>
        </Card>
      )}

      {sections.check && <CheckCard check={check} unsaved={changed} />}
    </div>
  );
}

function HookRow({ hook, hasAddress }: { hook: EmailHook; hasAddress: boolean }) {
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-medium text-text">
          Replies
          {!hook.enabled && <Badge tone="warning">Off</Badge>}
        </p>
        <Link
          to={`/triggers/${hook.trigger_id}`}
          className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text"
        >
          {hook.name}
          <ArrowRight className="size-3.5" aria-hidden />
        </Link>
      </div>
      {hook.url ? (
        <p className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2">
          <span className="break-all font-mono text-xs text-text">{maskedUrl(hook.url)}</span>
          <CopyButton value={hook.url} label="Copy the URL for replies" />
        </p>
      ) : hook.problem ? (
        <Callout tone="warning">{hook.problem} Open the trigger to put it right.</Callout>
      ) : (
        <Callout tone="warning">
          {hasAddress ? "This URL cannot be built yet. Save the settings again." : "Set the public address to see this URL."}
        </Callout>
      )}
      <p className="text-xs text-text-muted">
        Paste this in SendGrid: Settings, Inbound Parse, the reply domain as the receiving domain. Leave "POST the raw,
        full MIME message" off, and turn "Check incoming emails for spam" on.
      </p>
      <p className="text-xs text-text-muted">
        SendGrid drops a reply it cannot deliver here after 3 days. Keep this URL in SendGrid up to date.
      </p>
      <p className="text-xs text-text-muted">
        {hook.last_received_at ? `Last reply: ${relativeTime(hook.last_received_at)}.` : "Nothing has arrived yet."}
      </p>
    </div>
  );
}

/** The check of the SendGrid account: only for SendGrid, and only once it is the saved provider. */
function CheckCard({ check, unsaved }: { check: ReturnType<typeof useEmailCheck>; unsaved: boolean }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Check"
        description="Reads your SendGrid account and the reply domain's DNS, and says what is still wrong. It changes nothing, here or at SendGrid."
        action={
          <Button variant="outline" loading={check.isPending} disabled={unsaved} onClick={() => check.mutate()}>
            <Stethoscope />
            Check now
          </Button>
        }
      />
      <CardBody>
        {unsaved ? (
          <p className="text-xs text-text-muted">Save first. The check reads the settings as they are saved.</p>
        ) : check.error ? (
          <Callout tone="danger">
            {check.error instanceof ApiError ? check.error.message : "The check could not run. Please try again."}
          </Callout>
        ) : check.data ? (
          <CheckResult check={check.data} />
        ) : (
          <p className="text-xs text-text-muted">
            The key and its account, the from address, domain authentication, the reply domain's MX record, the
            allowed list, and SendGrid's Inbound Parse.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function CheckResult({ check }: { check: EmailCheck }) {
  return (
    <div className="space-y-3">
      <p className="flex flex-wrap items-center gap-2 text-sm text-text">
        <Badge tone={check.ok ? "success" : "warning"}>{checkSummary(check)}</Badge>
        <span className="text-xs text-text-muted">Checked {relativeTime(check.checked_at)}.</span>
      </p>
      <ul className="divide-y divide-border rounded-md border border-border">
        {check.items.map((item, index) => (
          <li key={`${item.code}-${index}`} className="flex items-start gap-2 px-3 py-2">
            {item.ok ? (
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success-9" aria-label="Right" />
            ) : (
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning-9" aria-label="Needs attention" />
            )}
            <p className="min-w-0 text-xs text-text">{item.detail}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
